// ============================================================
// MIDAS V3 — machine_evidence dynamic-snapshot viewer (ME-MACHINEID-RENDER SP-4)
// ============================================================
// The chat-host (a) renderer for a `machine_evidence` artifact (question-focused
// card: hero + supporting[] + deep-link) under the LOCKED ME-CONTRACT shape. A
// THIN wrapper mirroring MidasArtifactMachineCardV3 — it parses `tab.artifact.data`
// into the SP-1a model and COMPOSES the SP-1a honesty atoms/engine; it re-derives
// NOTHING (§7A flag #1: freshness comes from `data.freshness.state` via
// resolveFreshness, never recomputed from age_seconds).
//
// `MachineEvidenceArtifactData` has NO `machine` identity block, so the header uses
// the artifact TITLE (never resolveIdentityTitle). The GUID guard still protects
// every rendered figure/source string via the atoms' blanket looksLikeGuid strip.
//
// GAP-10: `machine_evidence` is NOT emitted live this milestone and `frozen` is
// always {is_frozen:false, frozen_at:null}. The snapshot/frozen chrome, the drift
// line, and the "take a new snapshot" button are all built but render in their
// DORMANT state against the static oracle — present + correct, NOT gated on live
// emission. No drift number is ever fabricated. Provenance/exception reuse the SAME
// synthetic single-tab adapter SP-2 uses (wrap hero+supporting into a one-tab
// MachineCardArtifactData) — no parallel footer/banner path.
import React, { useMemo } from "react";

import type {
  Figure,
  MachineCardArtifactData,
  MachineEvidenceArtifactData,
} from "../../../../../shared/components/machineEvidence";
import {
  Breakdown,
  CoverageValue,
  ExceptionBanner,
  FigureTile,
  FreshnessBanner,
  ProvenanceFooter,
  VerdictChip,
  buildProvenanceFooter,
  deriveExceptionBanner,
  formatIsoDateDDMMYYYY,
  resolveBreakdown,
  resolveCoverage,
  resolveFreshness,
  resolveVerdict,
} from "../../../../../shared/components/machineEvidence";
import { useAppDispatch, useAppSelector } from "../../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../../shared/hooks/useTranslation";
import { dt } from "../../../../../shared/themes/redesignTokens";
import { ArtifactTab } from "../../../midasPageModels";
import { openArtifact } from "../../../midasPageSlice";
import { MACHINE_CARD_ARM_TAB_SLUG, SIX_TAB_LABELS } from "./machineCardConstants";
import { machineArtifactDisplayTitle } from "./machineArtifactTitles";

export interface MidasArtifactMachineEvidenceV3Props {
  tab: ArtifactTab;
}

const containerStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 12,
  padding: 16,
  overflow: "auto",
  flex: 1,
  minHeight: 0,
  fontFamily: dt.font,
  color: dt.textPrimary,
};

const eyebrow: React.CSSProperties = {
  fontSize: 11,
  color: dt.textMuted,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  fontFamily: dt.fontMono,
};

const measureBasisStyle: React.CSSProperties = {
  fontFamily: dt.fontMono,
  fontSize: 10,
  color: dt.textMuted,
  padding: "0 5px",
  borderRadius: dt.radiusPill,
  border: `1px solid ${dt.cardBorder}`,
};

const chipStyle: React.CSSProperties = {
  padding: "4px 12px",
  borderRadius: dt.radiusPill,
  fontFamily: dt.font,
  fontSize: 12,
  fontWeight: 600,
  color: dt.textPrimary,
  background: dt.blueSoft,
  border: `1px solid ${dt.cardBorder}`,
  cursor: "pointer",
};

const dormantChipStyle: React.CSSProperties = {
  ...chipStyle,
  color: dt.textMuted,
  background: dt.inputBg,
  border: `1px solid ${dt.cardBorder}`,
  cursor: "not-allowed",
  opacity: 0.6,
};

const Empty: React.FC<{ message: string }> = ({ message }) => (
  <div
    style={{
      display: "flex",
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      padding: "40px 24px",
      fontFamily: dt.font,
      fontSize: 13,
      color: dt.textMuted,
    }}
  >
    {message}
  </div>
);

// Hero — the single number that answers the question. Mirrors the SP-2 Hero
// composition (coverage + unit + denominator + Breakdown) plus a measure_basis
// token when present (the chuck_clamp oracle carries commanded/measured; null on
// MASH — GAP-11). Rendered as a snapshot (no window projection).
const EvidenceHero: React.FC<{ figure: Figure; dim: boolean }> = ({ figure, dim }) => {
  const coverage = resolveCoverage(figure);
  const breakdown = resolveBreakdown(figure.breakdown);
  return (
    <div className="mev-hero" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ fontSize: 12, color: dt.textSecondary }}>{figure.label}</span>
        {figure.measure_basis ? (
          <span className="mev-measure-basis" style={measureBasisStyle}>
            {figure.measure_basis}
          </span>
        ) : null}
      </div>
      <div style={{ fontSize: 30, lineHeight: 1.1 }}>
        <CoverageValue resolution={coverage} unit={figure.unit} denominator={figure.denominator} dim={dim} />
      </div>
      {breakdown ? <Breakdown resolution={breakdown} /> : null}
    </div>
  );
};

const MidasArtifactMachineEvidenceV3: React.FC<MidasArtifactMachineEvidenceV3Props> = ({ tab }) => {
  const { translate } = useTranslation("machineEvidence");
  const dispatch = useAppDispatch();
  const artifactTabs = useAppSelector((s) => s.midasPage.artifactTabs);

  const data = tab.artifact.data as unknown as MachineEvidenceArtifactData | undefined;

  // Synthetic single-tab card adapter — reuse the SP-2 provenance/exception
  // engines with NO parallel path. machine_evidence has no identity block, so
  // `machine` is undefined; the GUID guard still strips any GUID-shaped token.
  const syntheticCard = useMemo<MachineCardArtifactData | null>(() => {
    if (!data || !data.hero) return null;
    return {
      machine: undefined,
      freshness: data.freshness,
      sources: data.sources,
      tabs: [{ figures: [data.hero, ...(data.supporting ?? [])] }],
    } as unknown as MachineCardArtifactData;
  }, [data]);

  if (!data || !data.hero || !syntheticCard) {
    return (
      <Empty message={translate("evidence.empty", { defaultValue: "No evidence in this card yet." }) as string} />
    );
  }

  const freshness = resolveFreshness(data.freshness);
  const verdict = resolveVerdict(data.verdict);
  const exception = deriveExceptionBanner(syntheticCard);
  const footerEntries = buildProvenanceFooter(syntheticCard);
  const frozen = data.frozen ?? { is_frozen: false, frozen_at: null };

  // Deep-link — the sibling machine_card among the open dock tabs (the deep-link
  // target tab; "arm" for MASH). Chip is DISABLED when no sibling is open.
  const targetSlug = data.links_to?.machine_card_tab || MACHINE_CARD_ARM_TAB_SLUG;
  const hasDeepLink = !!data.links_to?.machine_card_tab;
  const sibling = artifactTabs.find((t) => t.artifact.type === "machine_card");
  const targetLabel = translate(`tab.${targetSlug}`, {
    defaultValue: SIX_TAB_LABELS[targetSlug] || targetSlug,
  }) as string;

  const handleDeepLink = () => {
    if (!sibling) return;
    // SAME opener pattern as SP-3 (ChatBotMessageV3): focus the sibling card on
    // the deep-link tab via the `_deepLinkTabSlug` client render hint.
    dispatch(
      openArtifact({
        type: "machine_card",
        title: sibling.artifact.title,
        source: sibling.artifact.source,
        url: sibling.artifact.url || "",
        page: sibling.artifact.page,
        data: { ...(sibling.artifact.data as Record<string, unknown>), _deepLinkTabSlug: targetSlug },
      })
    );
  };

  return (
    <div className="mev-machine-evidence" style={containerStyle}>
      {/* 1. Header — the artifact TITLE (machine_evidence has NO identity block).
          ME-CARDS-R1 A1: rendered through the display-title helper, so a
          GUID-bearing bot title degrades to "<subsystem> · Machine evidence"
          instead of leaking the GUID into the header. */}
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <span style={{ fontSize: 17, fontWeight: 600 }}>
          {machineArtifactDisplayTitle(tab.artifact, translate)}
        </span>
        <span style={{ fontSize: 11, color: dt.textMuted, fontFamily: dt.fontMono, letterSpacing: "0.04em" }}>
          {translate("card.subtitle", { defaultValue: "MASH machine evidence" }) as string}
        </span>
      </div>

      {/* 2. Freshness — trusted bot state; two-clock backfill split when present. */}
      <FreshnessBanner resolution={freshness} />

      {/* 3. Promoted exception banner — DARK feed OR non-zero hard alarm figure. */}
      {exception.promote ? <ExceptionBanner resolution={exception} /> : null}

      {/* 4. Verdict — object; unknown/stale = dignified slate, never error. */}
      <VerdictChip resolution={verdict} />

      {/* 5. Hero + 8. dormant drift line (below the hero — GAP-10, no live delta). */}
      <EvidenceHero figure={data.hero} dim={freshness.dim} />
      <span className="mev-drift" data-dormant="drift" style={{ fontSize: 11, color: dt.textMuted, fontStyle: "italic" }}>
        {translate("evidence.driftUnavailable", {
          defaultValue: "Live drift unavailable — snapshot only this milestone",
        }) as string}
      </span>

      {/* 6. Supporting grid — 2-up FigureTiles; measure_basis shown when present. */}
      {data.supporting && data.supporting.length > 0 ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 12 }}>
          {data.supporting.map((f) => (
            <FigureTile key={f.id} figure={f} cardFreshness={data.freshness} showBreakdown showMeasureBasis />
          ))}
        </div>
      ) : null}

      {/* 7. Snapshot / frozen chrome + 9. dormant re-capture + 10. deep-link chip. */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", paddingTop: 4 }}>
        {frozen.is_frozen ? (
          <span className="mev-frozen" data-frozen="true" style={{ ...eyebrow, color: dt.textSecondary }}>
            {translate("evidence.frozenAt", {
              at: formatIsoDateDDMMYYYY(frozen.frozen_at),
              defaultValue: "Frozen at {{at}}",
            }) as string}
          </span>
        ) : (
          <span className="mev-snapshot" data-frozen="false" style={eyebrow}>
            {translate("evidence.snapshot", { defaultValue: "Snapshot" }) as string}
          </span>
        )}

        {/* Take a new snapshot — DORMANT: no live re-capture wired this milestone. */}
        <button
          type="button"
          className="mev-recapture"
          data-dormant="recapture"
          disabled
          title={translate("evidence.recaptureDisabled", {
            defaultValue: "Live re-capture not wired this milestone",
          }) as string}
          style={dormantChipStyle}
        >
          {translate("evidence.takeNewSnapshot", { defaultValue: "Take a new snapshot" }) as string}
        </button>

        {hasDeepLink ? (
          <button
            type="button"
            className="mev-deeplink"
            data-deeplink-target={targetSlug}
            disabled={!sibling}
            onClick={handleDeepLink}
            style={sibling ? chipStyle : dormantChipStyle}
          >
            {translate("evidence.openTab", { tab: targetLabel, defaultValue: "Open {{tab}} tab →" }) as string}
          </button>
        ) : null}
      </div>

      {/* 11. Provenance footer — signal_keys only, GUID-guarded; no [[tel:]] chips. */}
      <ProvenanceFooter entries={footerEntries} asOf={data.freshness?.as_of} />
    </div>
  );
};

export default MidasArtifactMachineEvidenceV3;
